import { useEffect, useRef, useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

declare global { interface Window { __mapPerfLog?: boolean } }
import { usePlayer } from '../context/PlayerContext'
import axios from '../api/client'
import { useToast } from '../context/ToastContext'
import { ReleaseFilters } from '../components/releases/ReleaseFilters'
import { ReleaseGridCard } from '../components/releases/ReleaseGrid'
import { Select, SearchInput } from '../components/common'
import { useGenres, useStyles, useWantlist, useCollection } from '../hooks'
import type { ReleaseFilters as ReleaseFiltersType, Release } from '../types/release'
import styles from './MapPage.module.css'
import pageStyles from './PageStyles.module.css'
import { getCachedBinary, setCachedBinary } from '../utils/mapCache'
import { createPointsGL, type PointsGL } from '../utils/mapGL'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface MapPoint {
  id: number
  x: number
  y: number
  idx?: number
  g: string | null
  score?: number
  matchScore?: number
  rating?: number
  want?: number
  median?: number
  year?: number
  styles?: number[]
  novelty?: number
}

// A cluster "island" (or coarse "continent") from /map/clusters — geometry and
// labels come straight from scripts/build_music_map.py, not the frontend.
interface Cluster {
  id: number
  level: number          // 1 = island, 0 = continent
  parentId: number | null
  rank: number
  label: string
  topStyles: string[]
  genre: string
  count: number
  x: number
  y: number
  hull: [number, number][]
  hue: number            // 0..360
  subtitle?: string      // island era/country, e.g. "1988–1993 · US"
  r?: number             // world radius (derived from hull) for glow sizing
}

interface AudioTagProfile {
  has_audio?: boolean
  predicted_genres?: Record<string, number>
  predicted_styles?: Record<string, number>
  confidence?: number
  tag_agreement?: number
}

interface Camera { cx: number; cy: number; zoom: number }
type HighlightMode = 'none' | 'search' | 'filter' | 'wantlist' | 'collection'

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------
const MONO_U32 = (150 | (155 << 8) | (170 << 16) | (210 << 24)) >>> 0

// HSL → little-endian RGBA Uint32 (R | G<<8 | B<<16 | A<<24)
function hslToU32(h: number, s: number, l: number, a: number): number {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const hp = (((h % 360) + 360) % 360) / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  let r = 0, g = 0, b = 0
  if (hp < 1) { r = c; g = x } else if (hp < 2) { r = x; g = c }
  else if (hp < 3) { g = c; b = x } else if (hp < 4) { g = x; b = c }
  else if (hp < 5) { r = x; b = c } else { r = c; b = x }
  const m = l - c / 2
  const R = Math.round((r + m) * 255), G = Math.round((g + m) * 255), B = Math.round((b + m) * 255)
  return (R | (G << 8) | (B << 16) | (a << 24)) >>> 0
}
const setAlpha = (u32: number, a: number) => ((u32 & 0x00ffffff) | (a << 24)) >>> 0

// Brand colormap for the optional value overlays (rating/price/year/…)
type ColorMode = 'none' | 'score' | 'match' | 'rating' | 'want' | 'median' | 'year' | 'novelty'
const COLOR_MODE_OPTIONS: { value: ColorMode; label: string }[] = [
  { value: 'none',    label: 'Color: Regions' },
  { value: 'rating',  label: 'Bayesian Rating' },
  { value: 'score',   label: 'Gem Score' },
  { value: 'match',   label: 'Match Score' },
  { value: 'want',    label: 'Want' },
  { value: 'median',  label: 'Price' },
  { value: 'year',    label: 'Year' },
  { value: 'novelty', label: 'Audio Novelty' },
]
const BRAND_CMAP_STOPS: [number, number, number, number][] = [
  [0.000,  20,  30, 140],
  [0.333,  15, 120, 200],
  [0.667,  40, 200, 120],
  [1.000, 202, 255,  71],
]
function buildLUT(stops: [number, number, number, number][], alpha: number): Uint32Array {
  const lut = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    const t = i / 255
    let lo = stops[0], hi = stops[stops.length - 1]
    for (let s = 0; s < stops.length - 1; s++) {
      if (t <= stops[s + 1][0]) { lo = stops[s]; hi = stops[s + 1]; break }
    }
    const f = (hi[0] === lo[0]) ? 0 : (t - lo[0]) / (hi[0] - lo[0])
    const r = Math.round(lo[1] + f * (hi[1] - lo[1]))
    const g = Math.round(lo[2] + f * (hi[2] - lo[2]))
    const b = Math.round(lo[3] + f * (hi[3] - lo[3]))
    lut[i] = (r | (g << 8) | (b << 16) | (alpha << 24)) >>> 0
  }
  return lut
}
const BRAND_COLORMAP = {
  lut: buildLUT(BRAND_CMAP_STOPS, 235),
  css: `linear-gradient(to right,${BRAND_CMAP_STOPS.map(([, r, g, b]) => `rgb(${r},${g},${b})`).join(',')})`,
}

const topAudioTerms = (terms?: Record<string, number>, limit = 4) =>
  Object.entries(terms ?? {})
    .filter(([, v]) => Number.isFinite(v))
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)

// ---------------------------------------------------------------------------
// World bounds + spatial structures (proven plumbing, reused)
// ---------------------------------------------------------------------------
const WORLD_MIN = -1000
const WORLD_MAX = 1000
const GRID_CELL = 40
const RENDER_CELL = 48
// Min spacing between points guaranteed by collision relaxation in
// build_music_map.py (--min-dist). Dots are drawn just under this so they never
// overlap at any zoom. Keep in sync with the pipeline default.
const DOT_WORLD = 1.4

class SpatialGrid {
  private cells = new Map<number, MapPoint[]>()
  private key(gx: number, gy: number): number { return gx * 100003 + gy }
  clear() { this.cells.clear() }
  insert(p: MapPoint) {
    const k = this.key(Math.floor(p.x / GRID_CELL), Math.floor(p.y / GRID_CELL))
    let cell = this.cells.get(k)
    if (!cell) { cell = []; this.cells.set(k, cell) }
    cell.push(p)
  }
  findNearest(wx: number, wy: number, worldRadius: number): MapPoint | null {
    const minGX = Math.floor((wx - worldRadius) / GRID_CELL)
    const maxGX = Math.floor((wx + worldRadius) / GRID_CELL)
    const minGY = Math.floor((wy - worldRadius) / GRID_CELL)
    const maxGY = Math.floor((wy + worldRadius) / GRID_CELL)
    let best: MapPoint | null = null, bestD2 = worldRadius * worldRadius
    for (let gx = minGX; gx <= maxGX; gx++) {
      for (let gy = minGY; gy <= maxGY; gy++) {
        const cell = this.cells.get(this.key(gx, gy))
        if (!cell) continue
        for (const p of cell) {
          const dx = p.x - wx, dy = p.y - wy
          const d2 = dx * dx + dy * dy
          if (d2 < bestD2) { bestD2 = d2; best = p }
        }
      }
    }
    return best
  }
}

class RenderIndex {
  private cells: Uint32Array[] = []
  private cellsByKey = new Map<number, Uint32Array>()
  build(px: Float32Array, py: Float32Array) {
    const byCell = new Map<number, number[]>()
    for (let i = 0; i < px.length; i++) {
      const gx = Math.floor((px[i] - WORLD_MIN) / RENDER_CELL)
      const gy = Math.floor((py[i] - WORLD_MIN) / RENDER_CELL)
      const k = gx * 100003 + gy
      let cell = byCell.get(k)
      if (!cell) { cell = []; byCell.set(k, cell) }
      cell.push(i)
    }
    this.cellsByKey = new Map()
    this.cells = Array.from(byCell.entries(), ([key, values]) => {
      const cell = Uint32Array.from(values)
      this.cellsByKey.set(key, cell)
      return cell
    })
  }
  visibleCells(cx: number, cy: number, zoom: number, w: number, h: number): Uint32Array[] {
    if (!this.cells.length) return []
    const minX = cx - (w / 2) / zoom, maxX = cx + (w / 2) / zoom
    const minY = cy - (h / 2) / zoom, maxY = cy + (h / 2) / zoom
    if (minX <= WORLD_MIN && maxX >= WORLD_MAX && minY <= WORLD_MIN && maxY >= WORLD_MAX) return this.cells
    const out: Uint32Array[] = []
    const minGX = Math.floor((Math.max(minX, WORLD_MIN) - WORLD_MIN) / RENDER_CELL)
    const maxGX = Math.floor((Math.min(maxX, WORLD_MAX) - WORLD_MIN) / RENDER_CELL)
    const minGY = Math.floor((Math.max(minY, WORLD_MIN) - WORLD_MIN) / RENDER_CELL)
    const maxGY = Math.floor((Math.min(maxY, WORLD_MAX) - WORLD_MIN) / RENDER_CELL)
    for (let gx = minGX; gx <= maxGX; gx++) {
      for (let gy = minGY; gy <= maxGY; gy++) {
        const cell = this.cellsByKey.get(gx * 100003 + gy)
        if (cell) out.push(cell)
      }
    }
    return out
  }
}

// ---------------------------------------------------------------------------
// Binary parse (v2): [4B headerLen][JSON header][4B count][id+9×f32][styles]
// ---------------------------------------------------------------------------
interface ParsedBinary { genres: string[]; styles: string[]; points: MapPoint[]; count: number; version?: string }
function parseBinaryPoints(buf: ArrayBuffer): ParsedBinary | null {
  try {
    const view = new DataView(buf)
    let offset = 0
    const headerLen = view.getUint32(offset, true); offset += 4
    const headerBytes = new Uint8Array(buf, offset, headerLen)
    const header = JSON.parse(new TextDecoder().decode(headerBytes)) as { genres: string[]; styles: string[]; version?: string }
    offset += headerLen
    const count = view.getUint32(offset, true); offset += 4
    const fixedOffset = offset
    offset += count * 40
    const styleView = new DataView(buf, offset)
    let si = 0
    const points: MapPoint[] = new Array(count)
    for (let i = 0; i < count; i++) {
      const pOff = fixedOffset + i * 40
      let pstyles: number[] | undefined
      const styleCount = styleView.getUint8(si); si += 1
      if (styleCount > 0) {
        pstyles = new Array(styleCount)
        for (let j = 0; j < styleCount; j++) { pstyles[j] = styleView.getUint16(si, true); si += 2 }
      }
      const gi = view.getFloat32(pOff + 12, true)
      const medRaw = view.getFloat32(pOff + 24, true)
      points[i] = {
        id: view.getInt32(pOff, true),
        x: view.getFloat32(pOff + 4, true),
        y: view.getFloat32(pOff + 8, true),
        g: gi >= 0 ? header.genres[gi] : null,
        score: view.getFloat32(pOff + 16, true),
        want: view.getFloat32(pOff + 20, true),
        median: medRaw !== 0 ? medRaw / 100 : undefined,
        year: view.getFloat32(pOff + 28, true),
        matchScore: view.getFloat32(pOff + 32, true),
        rating: view.getFloat32(pOff + 36, true),
        styles: pstyles,
      }
    }
    return { genres: header.genres, styles: header.styles, points, count, version: header.version }
  } catch { return null }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function MapPage() {
  const glCanvasRef = useRef<HTMLCanvasElement>(null)   // dots (WebGL, or 2D fallback)
  const fgCanvasRef = useRef<HTMLCanvasElement>(null)   // labels + hover/select (2D)
  const containerRef = useRef<HTMLDivElement>(null)

  const cameraRef = useRef<Camera>({ cx: 0, cy: 0, zoom: 0.4 })
  const boundsRef = useRef<{ cx: number; cy: number; w: number; h: number } | null>(null)
  const pointsRef = useRef<MapPoint[]>([])
  const pointsByIdRef = useRef<Map<number, MapPoint>>(new Map())
  const gridRef = useRef(new SpatialGrid())

  const ctxFgRef = useRef<CanvasRenderingContext2D | null>(null)
  const glRef = useRef<PointsGL | null>(null)
  const glReadyRef = useRef(false)                      // GL init attempted
  const glPosDirtyRef = useRef(false)
  const glColorDirtyRef = useRef(false)
  const glHlDirtyRef = useRef(false)
  // 2D fallback (only used if WebGL2 unavailable)
  const viewOCRef = useRef<OffscreenCanvas | null>(null)
  const viewImgRef = useRef<ImageData | null>(null)
  const glCtx2dRef = useRef<CanvasRenderingContext2D | null>(null)
  const data32Ref = useRef<Uint32Array>(new Uint32Array(0))
  const dprRef = useRef(1)
  const pxBufRef = useRef<Float32Array>(new Float32Array(0))
  const pyBufRef = useRef<Float32Array>(new Float32Array(0))
  const renderIndexRef = useRef(new RenderIndex())
  const pointIndexByIdRef = useRef<Map<number, number>>(new Map())
  const highlightedMainIndicesRef = useRef<number[]>([])

  // Cluster islands + per-point colour
  const clustersRef = useRef<Cluster[]>([])
  const islandsRef = useRef<Cluster[]>([])
  const continentsRef = useRef<Cluster[]>([])
  const neighborhoodsRef = useRef<Cluster[]>([])
  // Pre-sorted for label LOD (avoids per-frame sorts)
  const islandsSortedRef = useRef<Cluster[]>([])
  const continentsSortedRef = useRef<Cluster[]>([])
  const neighborhoodsSortedRef = useRef<Cluster[]>([])
  const islandColorRef = useRef<Uint32Array>(new Uint32Array(0)) // textured per-point colour
  const pointColorRef = useRef<Uint32Array>(new Uint32Array(0))  // resolved colour for current mode
  const dotWorldRef = useRef(DOT_WORLD)                          // grid cell size from /map/clusters

  const [isLoading, setIsLoading] = useState(true)
  const [loadingElapsed, setLoadingElapsed] = useState(0)
  const [noData, setNoData] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [pointCount, setPointCount] = useState(0)

  const [selectedPoint, setSelectedPoint] = useState<MapPoint | null>(null)
  const [selectedDetail, setSelectedDetail] = useState<Release | null>(null)
  const [popupPos, setPopupPos] = useState({ x: 0, y: 0 })
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)
  const [hoverPreview, setHoverPreview] = useState<{ r: Release; x: number; y: number } | null>(null)
  const hoveredIdRef = useRef<number | null>(null)

  const [colorMode, setColorMode] = useState<ColorMode>('none')
  const [colorLegend, setColorLegend] = useState<{ min: string; max: string } | null>(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [highlightMode, setHighlightMode] = useState<HighlightMode>('none')
  const [highlightCount, setHighlightCount] = useState(0)
  const [mapFilters, setMapFilters] = useState<ReleaseFiltersType>({})
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)

  const dragStart = useRef<{ mx: number; my: number; cx: number; cy: number } | null>(null)
  const touchStartRef = useRef<{ touches: { x: number; y: number }[]; cx: number; cy: number; zoom: number } | null>(null)

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wantlistRebuildTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const collectionRebuildTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hoverPrefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rafRef = useRef(0)
  const zoomAnimRef = useRef(0)
  const targetCameraRef = useRef<Camera | null>(null)
  const isLightRef = useRef(false)
  // App tokens read from CSS so the canvas matches the rest of tenwords in both themes.
  const themeRef = useRef({ bg: '#1f1f1f', ink: '#ffffff', muted: '#9a9a9a', accent: [202, 255, 71] })

  useGenres()
  useStyles()

  const [searchParams] = useSearchParams()
  const { isPlaying, currentTrack } = usePlayer()
  const { showToast } = useToast()
  const { ids: wantlistIds } = useWantlist()
  const { ids: collectionIds } = useCollection()
  const wantlist = useMemo(() => new Set(wantlistIds), [wantlistIds])
  const collectionSet = useMemo(() => new Set(collectionIds), [collectionIds])

  const highlightModeRef = useRef<HighlightMode>('none')
  const matchedIdsRef = useRef<Set<number>>(new Set())
  const wantlistRef = useRef<Set<number>>(new Set())
  const collectionSetRef = useRef<Set<number>>(new Set())
  const selectedPointRef = useRef<MapPoint | null>(null)
  const stylesListRef = useRef<string[]>([])
  const releaseCacheRef = useRef<Map<number, Release>>(new Map())

  const colorModeRef = useRef<ColorMode>('none')
  const colorValuesRef = useRef<Uint8Array | null>(null)
  const noveltyLoadedRef = useRef(false)

  const [audioNeighbors, setAudioNeighbors] = useState<Release[] | null>(null)
  const [audioNeighborsLoading, setAudioNeighborsLoading] = useState(false)
  const [selectedAudioProfile, setSelectedAudioProfile] = useState<AudioTagProfile | null>(null)

  // Focus a specific release via ?releaseId=
  useEffect(() => {
    if (isLoading) return
    const rid = Number(searchParams.get('releaseId'))
    if (!rid) return
    const pt = pointsByIdRef.current.get(rid)
    if (!pt) return
    cameraRef.current = { cx: pt.x, cy: pt.y, zoom: 3 }
    requestDraw()
    setTimeout(() => openPopup(pt), 50)
  }, [isLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  // Theme observer
  useEffect(() => {
    const update = () => {
      const light = document.documentElement.getAttribute('data-bs-theme') === 'light'
      const cs = getComputedStyle(document.documentElement)
      const v = (name: string, fallback: string) => cs.getPropertyValue(name).trim() || fallback
      themeRef.current = {
        bg: v('--stonks-bg-content', '#1f1f1f'),
        ink: v('--stonks-text-primary', '#ffffff'),
        muted: v('--stonks-text-muted', '#9a9a9a'),
        accent: v('--accent-rgb', '202, 255, 71').split(',').map(Number),
      }
      if (light !== isLightRef.current) { isLightRef.current = light; scheduleAssignColors() }
      requestDraw()
    }
    update()
    const obs = new MutationObserver(update)
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] })
    return () => obs.disconnect()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLoading) return
    setLoadingElapsed(0)
    const id = setInterval(() => setLoadingElapsed(s => s + 1), 1000)
    return () => clearInterval(id)
  }, [isLoading])

  useEffect(() => {
    wantlistRef.current = wantlist
    if (wantlistRebuildTimerRef.current) clearTimeout(wantlistRebuildTimerRef.current)
    wantlistRebuildTimerRef.current = setTimeout(() => { buildHlBuf(); requestDraw() }, 250)
  }, [wantlist]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    collectionSetRef.current = collectionSet
    if (collectionRebuildTimerRef.current) clearTimeout(collectionRebuildTimerRef.current)
    collectionRebuildTimerRef.current = setTimeout(() => { buildHlBuf(); requestDraw() }, 250)
  }, [collectionSet]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { selectedPointRef.current = selectedPoint; requestDraw() }, [selectedPoint])

  const getCssDims = () => {
    const c = containerRef.current
    return c ? { w: c.clientWidth, h: c.clientHeight } : { w: 0, h: 0 }
  }

  const buildHlBuf = () => {
    const mode = highlightModeRef.current
    const ids =
      mode === 'search' || mode === 'filter' ? matchedIdsRef.current :
      mode === 'wantlist' ? wantlistRef.current :
      mode === 'collection' ? collectionSetRef.current : null
    const mainHighlighted: number[] = []
    if (ids && mode !== 'none') {
      const indexById = pointIndexByIdRef.current
      for (const id of ids) {
        const idx = indexById.get(id)
        if (idx !== undefined) mainHighlighted.push(idx)
      }
    }
    highlightedMainIndicesRef.current = mainHighlighted
    glHlDirtyRef.current = true
    setHighlightCount(mainHighlighted.length)
  }

  useEffect(() => { highlightModeRef.current = highlightMode; buildHlBuf(); requestDraw() }, [highlightMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Per-point colour resolution ───────────────────────────────────────
  const rebuildPointColors = () => {
    const n = pointsRef.current.length
    if (!n) { pointColorRef.current = new Uint32Array(0); return }
    const mode = colorModeRef.current
    const colorVals = colorValuesRef.current
    if (mode !== 'none' && colorVals) {
      const out = new Uint32Array(n)
      const lut = BRAND_COLORMAP.lut
      for (let i = 0; i < n; i++) out[i] = lut[colorVals[i]]
      pointColorRef.current = out
    } else if (islandColorRef.current.length === n) {
      pointColorRef.current = islandColorRef.current
    } else {
      pointColorRef.current = new Uint32Array(n).fill(MONO_U32)
    }
    glColorDirtyRef.current = true
  }

  // Calm, mostly neutral dots: a faint tint per region (an island's parent) so
  // structure reads at a glance, plus slight lightness grain. Strong colour is
  // reserved for state (highlights, value overlays).
  const assignIslandColors = () => {
    const islands = islandsRef.current
    const n = pointsRef.current.length
    if (!islands.length || !n) return
    const cxA = new Float64Array(islands.length)
    const cyA = new Float64Array(islands.length)
    const hueA = new Float64Array(islands.length)
    const regionHue = new Map(continentsRef.current.map(c => [c.id, c.hue]))
    for (let k = 0; k < islands.length; k++) {
      cxA[k] = islands[k].x; cyA[k] = islands[k].y
      hueA[k] = regionHue.get(islands[k].parentId ?? -1) ?? islands[k].hue
    }
    const light = isLightRef.current
    const px = pxBufRef.current, py = pyBufRef.current
    const pts = pointsRef.current
    const out = new Uint32Array(n)
    for (let i = 0; i < n; i++) {
      let best = 0, bestD = Infinity
      const x = px[i], y = py[i]
      for (let k = 0; k < cxA.length; k++) {
        const dx = x - cxA[k], dy = y - cyA[k]
        const d = dx * dx + dy * dy
        if (d < bestD) { bestD = d; best = k }
      }
      const hsh = (pts[i].id * 2654435761) >>> 0
      const j = ((hsh % 1000) / 1000) - 0.5            // -0.5..0.5  (lightness)
      const jh = (((hsh >>> 10) % 1000) / 1000) - 0.5  // -0.5..0.5  (hue)
      const L = light ? 0.40 + j * 0.10 : 0.64 + j * 0.10
      out[i] = hslToU32(hueA[best] + jh * 8, 0.24, L, 230)
    }
    islandColorRef.current = out
    rebuildPointColors()
  }

  // ── Draw ──────────────────────────────────────────────────────────────
  // 2D fallback dot stamping (only when WebGL2 is unavailable)
  const drawDots2D = (W: number, H: number, cx: number, cy: number, zoom: number, halfW: number, halfH: number, dotPx: number, hasHL: boolean) => {
    const ctx = glCtx2dRef.current
    if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    let viewOC = viewOCRef.current
    if (!viewOC || viewOC.width !== W || viewOC.height !== H) { viewOC = new OffscreenCanvas(W, H); viewOCRef.current = viewOC; viewImgRef.current = null }
    const vCtx = viewOC.getContext('2d')!
    let viewImg = viewImgRef.current
    if (!viewImg || viewImg.width !== W || viewImg.height !== H) { viewImg = vCtx.createImageData(W, H); viewImgRef.current = viewImg; data32Ref.current = new Uint32Array(viewImg.data.buffer) }
    const data32 = data32Ref.current; data32.fill(0)
    const pxBuf = pxBufRef.current, pyBuf = pyBufRef.current, pColor = pointColorRef.current
    const renderCells = renderIndexRef.current.visibleCells(cx, cy, zoom, W, H)
    const r = dotPx / 2, r2 = r * r, ri = Math.ceil(r)
    const stamp = (sx: number, sy: number, color: number) => {
      if (dotPx <= 1) { const x = sx | 0, y = sy | 0; if (x >= 0 && x < W && y >= 0 && y < H) data32[y * W + x] = color; return }
      const cxp = Math.round(sx), cyp = Math.round(sy)
      for (let dy = -ri; dy <= ri; dy++) { const y = cyp + dy; if (y < 0 || y >= H) continue; const row = y * W
        for (let dx = -ri; dx <= ri; dx++) { if (dx * dx + dy * dy > r2) continue; const x = cxp + dx; if (x < 0 || x >= W) continue; data32[row + x] = color } }
    }
    for (const cell of renderCells) for (let k = 0; k < cell.length; k++) {
      const i = cell[k]
      const sx = (pxBuf[i] - cx) * zoom + halfW; if (sx < -r || sx >= W + r) continue
      const sy = (pyBuf[i] - cy) * zoom + halfH; if (sy < -r || sy >= H + r) continue
      stamp(sx, sy, hasHL ? setAlpha(pColor[i], 38) : pColor[i])
    }
    const [ar, ag, ab] = themeRef.current.accent
    const accentU32 = (ar | (ag << 8) | (ab << 16) | (255 << 24)) >>> 0
    if (hasHL) { const hiArr = highlightedMainIndicesRef.current
      for (let h = 0; h < hiArr.length; h++) { const i = hiArr[h]
        const sx = (pxBuf[i] - cx) * zoom + halfW; if (sx < -r || sx >= W + r) continue
        const sy = (pyBuf[i] - cy) * zoom + halfH; if (sy < -r || sy >= H + r) continue
        stamp(sx, sy, accentU32) } }
    vCtx.putImageData(viewImg, 0, 0)
    ctx.imageSmoothingEnabled = false
    ctx.drawImage(viewOC, 0, 0, W, H)
  }

  const drawRef = useRef(() => {})
  drawRef.current = () => {
    const perfStart = window.__mapPerfLog ? performance.now() : 0
    const ctxFg = ctxFgRef.current
    if (!ctxFg) return
    const { w: W, h: H } = getCssDims()
    if (W === 0 || H === 0) return

    const { cx, cy, zoom } = cameraRef.current
    const halfW = W / 2, halfH = H / 2

    // ── Dots (WebGL, or 2D fallback); water is the container's CSS background ──
    const nPts = pxBufRef.current.length
    const hasHL = highlightModeRef.current !== 'none'
    // Continuous size (no rounding: the jittered lattice no longer aliases); the zoom^0.6 floor keeps mid-zoom islands legible.
    const dotPx = Math.min(20, Math.max(1, dotWorldRef.current * zoom * 0.9, 0.9 * Math.pow(zoom, 0.6)))
    if (glRef.current && nPts > 0) {
      const gl = glRef.current
      if (glPosDirtyRef.current) { gl.setPositions(pxBufRef.current, pyBufRef.current); glPosDirtyRef.current = false }
      if (glColorDirtyRef.current && pointColorRef.current.length === nPts) { gl.setColors(pointColorRef.current); glColorDirtyRef.current = false }
      if (glHlDirtyRef.current) { gl.setHighlight(hasHL ? highlightedMainIndicesRef.current : null); glHlDirtyRef.current = false }
      const [ar, ag, ab] = themeRef.current.accent
      gl.draw({ cx, cy, zoom, W, H, size: dotPx * dprRef.current, hlMode: hasHL, dim: 0.16, hlColor: [ar / 255, ag / 255, ab / 255] })
    } else if (nPts > 0 && pointColorRef.current.length === nPts) {
      drawDots2D(W, H, cx, cy, zoom, halfW, halfH, dotPx, hasHL)
    }

    // ── Labels + hover/selection (fg 2D) ──────────────────────────────────
    ctxFg.clearRect(0, 0, W, H)
    drawLabels(ctxFg, W, H, cx, cy, zoom)
    const hovId = hoveredIdRef.current
    const selId = selectedPointRef.current?.id ?? null
    if (hovId || selId) {
      for (const id of [hovId, selId]) {
        if (!id) continue
        const p = pointsByIdRef.current.get(id)
        if (!p) continue
        const sx = (p.x - cx) * zoom + halfW
        const sy = (p.y - cy) * zoom + halfH
        if (sx < -20 || sx > W + 20 || sy < -20 || sy > H + 20) continue
        const isSel = id === selId
        const r = isSel ? 9 : 6
        ctxFg.beginPath()
        ctxFg.arc(sx, sy, r, 0, Math.PI * 2)
        ctxFg.strokeStyle = isSel ? `rgb(${themeRef.current.accent.join(',')})` : themeRef.current.ink
        ctxFg.lineWidth = isSel ? 2 : 1.5
        ctxFg.stroke()
      }
    }

    if (window.__mapPerfLog) console.log(`  draw-frame: ${(performance.now() - perfStart).toFixed(1)}ms`)
  }

  // Place cluster labels with overlap avoidance + LOD, in the app's type scale:
  // regions small-caps muted, islands plain, neighbourhoods muted. A thin knockout
  // in the water colour keeps them legible over dots (no glow, no heavy outline).
  const measureCacheRef = useRef<Map<string, number>>(new Map())
  const drawLabels = (ctx: CanvasRenderingContext2D, W: number, H: number, cx: number, cy: number, zoom: number) => {
    const halfW = W / 2, halfH = H / 2
    const placed: { x: number; y: number; w: number; h: number }[] = []
    const { ink, muted, bg } = themeRef.current
    const mcache = measureCacheRef.current
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.lineJoin = 'round'
    ctx.lineWidth = 3; ctx.strokeStyle = bg
    const place = (label: string, x: number, y: number, size: number, weight: number, color: string, tracking: string, sub = '') => {
      const font = `${weight} ${size}px Syne, system-ui, sans-serif`
      ctx.font = font
      ctx.letterSpacing = tracking
      const key = font + tracking + '|' + label
      let w = mcache.get(key)
      if (w === undefined) { w = ctx.measureText(label).width; mcache.set(key, w) }
      const h = size + (sub ? 13 : 0)
      const box = { x: x - w / 2, y: y - size / 2, w, h }
      for (const p of placed) {
        if (Math.abs((p.x + p.w / 2) - x) < (p.w + w) / 2 + 12 &&
            Math.abs((p.y + p.h / 2) - (box.y + h / 2)) < (p.h + h) / 2 + 6) return false
      }
      placed.push(box)
      ctx.strokeText(label, x, y)
      ctx.fillStyle = color
      ctx.fillText(label, x, y)
      if (sub) {
        ctx.font = '500 10px Syne, system-ui, sans-serif'
        ctx.letterSpacing = '0px'
        ctx.strokeText(sub, x, y + size * 0.5 + 7)
        ctx.fillStyle = muted
        ctx.fillText(sub, x, y + size * 0.5 + 7)
      }
      return true
    }
    const onScreen = (x: number, y: number) => x > -80 && x < W + 80 && y > -40 && y < H + 40

    // LOD: regions (overview) → islands (mid) → neighbourhoods (deep).
    // Finer labels are placed first so they win space at their zoom.
    if (zoom >= 3.5) {
      const maxN = zoom < 6 ? 30 : 80
      let shown = 0
      for (const nb of neighborhoodsSortedRef.current) {
        if (shown >= maxN) break
        const x = (nb.x - cx) * zoom + halfW, y = (nb.y - cy) * zoom + halfH
        if (onScreen(x, y) && place(nb.label, x, y, 11, 500, muted, '0px')) shown++
      }
    }
    if (zoom >= 0.7 && zoom < 5) {
      const maxLabels = zoom < 1.5 ? 24 : zoom < 3 ? 48 : 90
      let shown = 0
      for (const isl of islandsSortedRef.current) {
        if (shown >= maxLabels) break
        const x = (isl.x - cx) * zoom + halfW, y = (isl.y - cy) * zoom + halfH
        if (onScreen(x, y) && place(isl.label, x, y, 13, 600, ink, '0px', zoom >= 1 ? isl.subtitle : '')) shown++
      }
    }
    if (zoom < 1.1) {
      for (const c of continentsSortedRef.current) {
        const x = (c.x - cx) * zoom + halfW, y = (c.y - cy) * zoom + halfH
        if (onScreen(x, y)) place(c.label.toUpperCase(), x, y, 11, 700, muted, '1.5px')
      }
    }
    ctx.letterSpacing = '0px'
  }

  const requestDraw = () => {
    if (rafRef.current) return
    rafRef.current = requestAnimationFrame(() => { rafRef.current = 0; drawRef.current() })
  }
  const drawNow = () => { cancelAnimationFrame(rafRef.current); rafRef.current = 0; drawRef.current() }

  const animateZoom = () => {
    const target = targetCameraRef.current
    if (!target) { zoomAnimRef.current = 0; return }
    const cur = cameraRef.current
    const t = 0.22
    const newCx = cur.cx + (target.cx - cur.cx) * t
    const newCy = cur.cy + (target.cy - cur.cy) * t
    const newZoom = cur.zoom * Math.exp(Math.log(target.zoom / cur.zoom) * t)
    const done = Math.abs(newZoom - target.zoom) / target.zoom < 0.003 &&
                 Math.abs(newCx - target.cx) < 1 && Math.abs(newCy - target.cy) < 1
    if (done) { cameraRef.current = target; targetCameraRef.current = null; zoomAnimRef.current = 0 }
    else { cameraRef.current = { cx: newCx, cy: newCy, zoom: newZoom }; zoomAnimRef.current = requestAnimationFrame(animateZoom) }
    requestDraw()
  }

  // ── Colour values for value overlays ──────────────────────────────────
  const computeColorValues = (mode: ColorMode) => {
    const points = pointsRef.current
    if (!points.length || mode === 'none') { colorValuesRef.current = null; setColorLegend(null); rebuildPointColors(); return }
    const raw = new Float32Array(points.length)
    for (let i = 0; i < points.length; i++) {
      const p = points[i]
      if      (mode === 'score')   raw[i] = p.score ?? 0
      else if (mode === 'match')   raw[i] = p.matchScore ?? 0
      else if (mode === 'rating')  raw[i] = p.rating ?? 0
      else if (mode === 'want')    raw[i] = p.want ?? 0
      else if (mode === 'median')  raw[i] = p.median ?? 0
      else if (mode === 'year')    raw[i] = p.year ?? 0
      else if (mode === 'novelty') raw[i] = p.novelty ?? 0
    }
    const nzIdx: number[] = []
    for (let i = 0; i < raw.length; i++) if (raw[i] > 0) nzIdx.push(i)
    nzIdx.sort((a, b) => raw[a] - raw[b])
    const n = nzIdx.length
    const out = new Uint8Array(points.length)
    if (n >= 100) {
      const scale = n > 1 ? 254 / (n - 1) : 0
      for (let rank = 0; rank < n; rank++) out[nzIdx[rank]] = 1 + Math.round(rank * scale)
    } else {
      const lo0 = n > 0 ? raw[nzIdx[0]] : 0
      const hi0 = n > 0 ? raw[nzIdx[n - 1]] : 0
      const range = Math.max(hi0 - lo0, 1e-6)
      for (let i = 0; i < raw.length; i++) out[i] = raw[i] <= 0 ? 0 : Math.max(1, Math.min(255, Math.round((raw[i] - lo0) / range * 254) + 1))
    }
    colorValuesRef.current = out
    const lo = n > 0 ? raw[nzIdx[Math.floor(n * 0.02)]] : 0
    const hi = n > 0 ? raw[nzIdx[Math.floor(n * 0.98)]] : 0
    const fmt = (v: number) => {
      if (mode === 'year')    return String(Math.round(v))
      if (mode === 'median')  return `$${v < 1000 ? v.toFixed(0) : (v / 1000).toFixed(1) + 'k'}`
      if (mode === 'want')    return String(Math.round(v))
      if (mode === 'rating')  return v.toFixed(2)
      if (mode === 'novelty') return (v * 100).toFixed(0) + '%'
      return v.toFixed(1)
    }
    setColorLegend({ min: fmt(lo), max: fmt(hi) })
    rebuildPointColors()
  }

  useEffect(() => {
    colorModeRef.current = colorMode
    if (colorMode === 'novelty' && !noveltyLoadedRef.current) {
      fetch('/api/releases/map/audio-novelty').then(r => r.json()).then(json => {
        if (json.success && json.data) {
          const data = json.data as Record<string, number>
          for (const p of pointsRef.current) {
            const agreement = data[p.id]
            if (agreement !== undefined) p.novelty = 1 - agreement
          }
          noveltyLoadedRef.current = true
          computeColorValues('novelty'); requestDraw()
        }
      }).catch(() => {})
    } else { computeColorValues(colorMode); requestDraw() }
  }, [colorMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Hydrate points ────────────────────────────────────────────────────
  const hydrateFromParsed = (styleList: string[], points: MapPoint[]) => {
    const grid = gridRef.current
    grid.clear()
    const byId = new Map<number, MapPoint>()
    const indexById = new Map<number, number>()
    const pts: MapPoint[] = []
    for (let i = 0; i < points.length; i++) {
      const p = points[i]
      if (p.x < WORLD_MIN || p.x > WORLD_MAX || p.y < WORLD_MIN || p.y > WORLD_MAX) continue
      p.idx = pts.length
      pts.push(p); byId.set(p.id, p); indexById.set(p.id, p.idx); grid.insert(p)
    }
    pointsRef.current = pts
    pointsByIdRef.current = byId
    pointIndexByIdRef.current = indexById
    const n = pts.length
    const pxBuf = new Float32Array(n), pyBuf = new Float32Array(n)
    for (let i = 0; i < n; i++) { pxBuf[i] = pts[i].x; pyBuf[i] = pts[i].y }
    pxBufRef.current = pxBuf; pyBufRef.current = pyBuf
    renderIndexRef.current.build(pxBuf, pyBuf)
    glPosDirtyRef.current = true
    highlightedMainIndicesRef.current = []
    stylesListRef.current = styleList
    setPointCount(n)
    let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity
    for (let i = 0; i < n; i++) {
      if (pxBuf[i] < x0) x0 = pxBuf[i]; if (pxBuf[i] > x1) x1 = pxBuf[i]
      if (pyBuf[i] < y0) y0 = pyBuf[i]; if (pyBuf[i] > y1) y1 = pyBuf[i]
    }
    const firstFit = !boundsRef.current
    if (n) boundsRef.current = { cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, w: x1 - x0, h: y1 - y0 }
    if (firstFit && n) cameraRef.current = fitCamera()
    rebuildPointColors()                 // immediate (mono until islands colour in)
    scheduleAssignColors()               // island colours, off the critical path
    if (colorModeRef.current !== 'none') computeColorValues(colorModeRef.current)
  }

  const hydratePoints = (genres: string[], raw: number[][]) => {
    const pts: MapPoint[] = []
    for (let i = 0; i < raw.length; i++) {
      const t = raw[i]
      const x = t[1], y = t[2]
      if (x < WORLD_MIN || x > WORLD_MAX || y < WORLD_MIN || y > WORLD_MAX) continue
      let pstyles: number[] | undefined
      if (t[8] && typeof t[8] === 'string' && t[8] !== '') pstyles = (t[8] as string).split(',').map(Number)
      pts.push({
        id: t[0], x, y, g: t[3] >= 0 ? genres[t[3]] : null,
        score: t[4], want: t[5], median: t[6] !== undefined ? t[6] / 100 : undefined,
        year: t[7], styles: pstyles, matchScore: t[9] as number | undefined, rating: t[10] as number | undefined,
      })
    }
    hydrateFromParsed(stylesListRef.current, pts)
  }

  // ── Load points (cache → binary → JSON) ───────────────────────────────
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const cached = await getCachedBinary()
        if (cached) {
          const parsed = parseBinaryPoints(cached.buf)
          if (parsed && parsed.count > 0 && !cancelled) {
            hydrateFromParsed(parsed.styles, parsed.points)
            setIsLoading(false); drawNow()
            fetch('/api/releases/map/points-bin').then(r => (!r.ok || r.status === 204) ? null : r.arrayBuffer()).then(buf => {
              if (!buf || cancelled) return
              const fresh = parseBinaryPoints(buf)
              if (!fresh) return
              if (fresh.version !== parsed.version || fresh.count !== parsed.count) {
                hydrateFromParsed(fresh.styles, fresh.points); drawNow()
              }
              setCachedBinary(buf, fresh.version)
            }).catch(() => {})
            return
          }
        }
        const res = await fetch('/api/releases/map/points-bin')
        if (res.ok && res.status !== 204) {
          const buf = await res.arrayBuffer()
          if (cancelled) return
          const parsed = parseBinaryPoints(buf)
          if (parsed && parsed.count > 0) {
            hydrateFromParsed(parsed.styles, parsed.points)
            setIsLoading(false); drawNow(); setCachedBinary(buf, parsed.version); return
          }
        }
        const jsonRes = await fetch('/api/releases/map/points')
        const data = await jsonRes.json()
        if (cancelled) return
        if (data.no_data) { setNoData(true); setIsLoading(false); return }
        stylesListRef.current = data.styles ?? []
        hydratePoints(data.genres ?? [], data.points ?? [])
        setIsLoading(false); drawNow()
      } catch { if (!cancelled) setIsLoading(false) }
    })()
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Run the (one-time, ~35M) per-point colour assignment off the critical path
  // so labels + glow paint immediately and never block on it.
  const colorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleAssignColors = () => {
    if (!islandsRef.current.length || !pointsRef.current.length) return
    if (colorTimerRef.current) clearTimeout(colorTimerRef.current)
    colorTimerRef.current = setTimeout(() => { assignIslandColors(); requestDraw() }, 0)
  }

  // ── Load island clusters (independent of the 36MB point payload so labels
  //    appear immediately) ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    fetch('/api/releases/map/clusters').then(r => r.json()).then(json => {
      if (cancelled || !json.success) return
      const clusters = (json.clusters ?? []) as Cluster[]
      if (typeof json.cell === 'number' && json.cell > 0) dotWorldRef.current = json.cell
      clustersRef.current = clusters
      const isls = clusters.filter(c => c.level === 1)
      for (const isl of isls) {
        let rr = 0
        for (const [hx, hy] of isl.hull) { const d = Math.hypot(hx - isl.x, hy - isl.y); if (d > rr) rr = d }
        isl.r = rr > 0 ? rr : Math.sqrt(isl.count) * 1.4
      }
      islandsRef.current = isls
      continentsRef.current = clusters.filter(c => c.level === 0)
      neighborhoodsRef.current = clusters.filter(c => c.level === 2)
      islandsSortedRef.current = [...isls].sort((a, b) => a.rank - b.rank)
      continentsSortedRef.current = [...continentsRef.current].sort((a, b) => b.count - a.count)
      neighborhoodsSortedRef.current = [...neighborhoodsRef.current].sort((a, b) => b.count - a.count)
      requestDraw()            // labels + glow paint now
      scheduleAssignColors()   // colours computed async (needs points too)
    }).catch(() => {})
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Detail ────────────────────────────────────────────────────────────
  const fetchDetail = async (point: MapPoint) => {
    const cached = releaseCacheRef.current.get(point.id)
    if (cached) setSelectedDetail(cached)
    else {
      setIsLoadingDetail(true); setSelectedDetail(null)
      try {
        const res = await fetch(`/api/releases/${point.id}`)
        const data = await res.json()
        if (data.release) { releaseCacheRef.current.set(point.id, data.release); setSelectedDetail(data.release) }
      } catch { showToast('Failed to load release details', 'error') }
      finally { setIsLoadingDetail(false) }
    }
    setAudioNeighbors(null); setSelectedAudioProfile(null); setAudioNeighborsLoading(true)
    try {
      const [neighborsRes, profileRes] = await Promise.all([
        fetch(`/api/releases/${point.id}/audio-neighbors?limit=5`),
        fetch(`/api/releases/${point.id}/audio-tags`),
      ])
      const neighborsData = await neighborsRes.json()
      if (neighborsData.success && neighborsData.releases?.length > 0) setAudioNeighbors(neighborsData.releases)
      const profileData = await profileRes.json()
      if (profileData.success && profileData.has_audio) setSelectedAudioProfile(profileData)
    } catch { /* silent */ }
    finally { setAudioNeighborsLoading(false) }
  }

  // ── Search ────────────────────────────────────────────────────────────
  const performSearch = async (query: string) => {
    if (!query.trim()) {
      matchedIdsRef.current = new Set()
      setHighlightMode(prev => prev === 'search' ? 'none' : prev)
      setHighlightCount(0); requestDraw(); return
    }
    try {
      const res = await fetch(`/api/releases/map/search?q=${encodeURIComponent(query)}&limit=500`)
      const data = await res.json()
      const ids = new Set<number>()
      const positions: { x: number; y: number }[] = []
      for (const r of (data.results ?? [])) { ids.add(r.id); positions.push({ x: r.x, y: r.y }) }
      matchedIdsRef.current = ids
      highlightModeRef.current = 'search'; buildHlBuf(); setHighlightMode('search')
      if (positions.length > 0) {
        const avgX = positions.reduce((s, p) => s + p.x, 0) / positions.length
        const avgY = positions.reduce((s, p) => s + p.y, 0) / positions.length
        targetCameraRef.current = { cx: avgX, cy: avgY, zoom: Math.max(cameraRef.current.zoom, 1.4) }
        if (!zoomAnimRef.current) zoomAnimRef.current = requestAnimationFrame(animateZoom)
      }
      requestDraw()
    } catch { /* silent */ }
  }

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    if (!searchQuery.trim()) { performSearch(''); return }
    searchDebounceRef.current = setTimeout(() => performSearch(searchQuery), 400)
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current) }
  }, [searchQuery]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resize + WebGL init ───────────────────────────────────────────────
  useEffect(() => {
    const glc = glCanvasRef.current, fg = fgCanvasRef.current, container = containerRef.current
    if (!glc || !fg || !container) return

    // Init the dot layer: WebGL2 if available, else a 2D context for the fallback.
    if (!glReadyRef.current) {
      glReadyRef.current = true
      const gl = createPointsGL(glc)
      if (gl) {
        glRef.current = gl
        glPosDirtyRef.current = glColorDirtyRef.current = glHlDirtyRef.current = true
      } else {
        glCtx2dRef.current = glc.getContext('2d')
      }
    }

    const resize = () => {
      const dpr = window.devicePixelRatio || 1
      dprRef.current = dpr
      const w = container.clientWidth, h = container.clientHeight
      for (const c of [glc, fg]) { c.width = w * dpr; c.height = h * dpr; c.style.width = `${w}px`; c.style.height = `${h}px` }
      const fctx = fg.getContext('2d'); if (fctx) { fctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctxFgRef.current = fctx }
      if (glRef.current) glRef.current.resize(w * dpr, h * dpr)
      else if (glCtx2dRef.current) glCtx2dRef.current.setTransform(dpr, 0, 0, dpr, 0, 0)
      drawNow()
    }
    resize()
    const ro = new ResizeObserver(resize); ro.observe(container)
    return () => ro.disconnect()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.code === 'KeyM') {
        e.preventDefault(); window.__mapPerfLog = !(window.__mapPerfLog ?? false)
        console.log(`🗺 Map perf logging: ${window.__mapPerfLog ? 'ON' : 'OFF'}`)
      }
    }
    window.addEventListener('keydown', handleKeyDown, { passive: false })
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // ── Helpers ───────────────────────────────────────────────────────────
  const worldToScreen = (wx: number, wy: number) => {
    const { w, h } = getCssDims(); const { cx, cy, zoom } = cameraRef.current
    return { sx: (wx - cx) * zoom + w / 2, sy: (wy - cy) * zoom + h / 2 }
  }
  const screenToWorld = (sx: number, sy: number) => {
    const { w, h } = getCssDims(); const { cx, cy, zoom } = cameraRef.current
    return { wx: (sx - w / 2) / zoom + cx, wy: (sy - h / 2) / zoom + cy }
  }
  const findNearest = (sx: number, sy: number, maxPx = 18): MapPoint | null => {
    const { wx, wy } = screenToWorld(sx, sy)
    return gridRef.current.findNearest(wx, wy, maxPx / cameraRef.current.zoom)
  }

  const openPopup = (p: MapPoint) => {
    const { sx, sy } = worldToScreen(p.x, p.y)
    const { w: cW, h: cH } = getCssDims()
    const POP_W = 240, POP_H = 360
    let px = sx + 16, py = sy - 150
    if (px + POP_W > cW - 8) px = sx - POP_W - 16
    if (px < 8) px = 8
    if (py < 8) py = 8
    if (py + POP_H > cH - 8) py = Math.max(8, cH - POP_H - 8)
    setPopupPos({ x: px, y: py })
    setHoverPreview(null)
    setSelectedPoint(p); setSelectedDetail(null)
    fetchDetail(p)
    axios.post('/activity', { action: 'release_view', metadata: { release_id: p.id } }).catch(() => {})
  }
  const closePopup = (force = false) => {
    if (!force && isPlaying && currentTrack?.releaseId === selectedPoint?.id) return
    setSelectedPoint(null); setSelectedDetail(null); setAudioNeighbors(null); setSelectedAudioProfile(null)
  }

  // ── Mouse / touch ─────────────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    dragStart.current = { mx: e.clientX, my: e.clientY, cx: cameraRef.current.cx, cy: cameraRef.current.cy }
    setIsDragging(false)
    setHoverPreview(null)
  }
  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = containerRef.current!.getBoundingClientRect()
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top
    if (dragStart.current) {
      const dx = e.clientX - dragStart.current.mx, dy = e.clientY - dragStart.current.my
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
        setIsDragging(true)
        const { zoom } = cameraRef.current
        cameraRef.current = { ...cameraRef.current, cx: dragStart.current.cx - dx / zoom, cy: dragStart.current.cy - dy / zoom }
        requestDraw()
      }
      return
    }
    const nearest = findNearest(sx, sy, 12)
    const newId = nearest?.id ?? null
    if (newId !== hoveredIdRef.current) {
      hoveredIdRef.current = newId
      containerRef.current!.style.cursor = newId ? 'pointer' : 'grab'
      requestDraw()
      if (hoverPrefetchTimerRef.current) clearTimeout(hoverPrefetchTimerRef.current)
      setHoverPreview(null)
      if (newId && nearest) {
        const pt = nearest
        hoverPrefetchTimerRef.current = setTimeout(() => {
          const show = (rel: Release) => {
            if (hoveredIdRef.current === pt.id && !selectedPointRef.current) {
              const { sx: psx, sy: psy } = worldToScreen(pt.x, pt.y)
              setHoverPreview({ r: rel, x: psx, y: psy })
            }
          }
          const cached = releaseCacheRef.current.get(pt.id)
          if (cached) { show(cached); return }
          fetch(`/api/releases/${pt.id}`).then(r => r.json()).then(data => {
            if (data.release) { releaseCacheRef.current.set(pt.id, data.release); show(data.release) }
          }).catch(() => {})
        }, 160)
      }
    }
  }
  const onMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    const wasDrag = isDragging
    dragStart.current = null; setIsDragging(false)
    if (wasDrag) return
    const rect = containerRef.current!.getBoundingClientRect()
    const nearest = findNearest(e.clientX - rect.left, e.clientY - rect.top, 20)
    if (nearest) openPopup(nearest); else closePopup()
    requestDraw()
  }
  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault()
    const rect = containerRef.current!.getBoundingClientRect()
    const mx = e.clientX - rect.left, my = e.clientY - rect.top
    const { w, h } = getCssDims()
    const base = targetCameraRef.current ?? cameraRef.current
    const halfW = w / 2, halfH = h / 2
    const worldX = (mx - halfW) / base.zoom + base.cx, worldY = (my - halfH) / base.zoom + base.cy
    // Proportional to scroll amount (gentle): handles both pixel trackpads and
    // line-mode mouse wheels; clamped so one big delta can't lurch the camera.
    const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY
    const factor = Math.min(1.5, Math.max(1 / 1.5, Math.exp(-dy * 0.0016)))
    const newZoom = Math.max(0.08, Math.min(120, base.zoom * factor))
    targetCameraRef.current = { cx: worldX - (mx - halfW) / newZoom, cy: worldY - (my - halfH) / newZoom, zoom: newZoom }
    if (!zoomAnimRef.current) zoomAnimRef.current = requestAnimationFrame(animateZoom)
  }

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartRef.current = {
      touches: Array.from(e.touches).map(t => ({ x: t.clientX, y: t.clientY })),
      cx: cameraRef.current.cx, cy: cameraRef.current.cy, zoom: cameraRef.current.zoom,
    }
  }
  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!touchStartRef.current) return
    const touches = Array.from(e.touches)
    if (touches.length === 1 && touchStartRef.current.touches.length === 1) {
      const dx = touches[0].clientX - touchStartRef.current.touches[0].x
      const dy = touches[0].clientY - touchStartRef.current.touches[0].y
      cameraRef.current = { ...cameraRef.current, cx: touchStartRef.current.cx - dx / cameraRef.current.zoom, cy: touchStartRef.current.cy - dy / cameraRef.current.zoom }
      requestDraw()
    } else if (touches.length === 2 && touchStartRef.current.touches.length >= 2) {
      const d0 = Math.hypot(touchStartRef.current.touches[0].x - touchStartRef.current.touches[1].x, touchStartRef.current.touches[0].y - touchStartRef.current.touches[1].y)
      const d1 = Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY)
      cameraRef.current = { ...cameraRef.current, zoom: Math.max(0.08, Math.min(120, touchStartRef.current.zoom * (d1 / Math.max(d0, 1)))) }
      requestDraw()
    }
  }
  const onTouchEnd = () => { touchStartRef.current = null }

  const zoomBy = (f: number) => {
    const base = targetCameraRef.current ?? cameraRef.current
    targetCameraRef.current = { ...base, zoom: Math.max(0.08, Math.min(120, base.zoom * f)) }
    if (!zoomAnimRef.current) zoomAnimRef.current = requestAnimationFrame(animateZoom)
  }
  // Whole archipelago in view (the layout isn't square, so a fixed zoom left half the screen empty).
  const fitCamera = (): Camera => {
    const b = boundsRef.current, { w, h } = getCssDims()
    if (!b || !w || !h) return { cx: 0, cy: 0, zoom: 0.4 }
    return { cx: b.cx, cy: b.cy, zoom: Math.max(0.08, 0.94 * Math.min(w / Math.max(b.w, 1), h / Math.max(b.h, 1))) }
  }
  const resetCamera = () => {
    targetCameraRef.current = fitCamera()
    if (!zoomAnimRef.current) zoomAnimRef.current = requestAnimationFrame(animateZoom)
  }
  const surpriseMe = () => {
    const islands = islandsRef.current
    if (!islands.length) return
    const pick = islands[Math.floor(Math.pow(Math.random(), 1.7) * islands.length)]
    targetCameraRef.current = { cx: pick.x, cy: pick.y, zoom: 3.2 }
    if (!zoomAnimRef.current) zoomAnimRef.current = requestAnimationFrame(animateZoom)
  }

  // ── Filters ───────────────────────────────────────────────────────────
  const hasActiveMapFilters = (f: ReleaseFiltersType) =>
    (f.genres?.length ?? 0) > 0 || (f.styles?.length ?? 0) > 0 ||
    f.year_from != null || f.year_to != null ||
    f.min_elevated_score != null || f.max_last_sold_days != null ||
    f.min_want != null || f.min_median != null || f.max_median != null

  const handleMapFiltersChange = async (newFilters: ReleaseFiltersType) => {
    setMapFilters(newFilters); setFilterPanelOpen(false)
    if (!hasActiveMapFilters(newFilters)) {
      matchedIdsRef.current = new Set()
      setHighlightMode(prev => prev === 'filter' ? 'none' : prev)
      setHighlightCount(0); requestDraw(); return
    }
    try {
      const ids = new Set<number>()
      const points = pointsRef.current
      const hasGenreFilter = (newFilters.genres?.length ?? 0) > 0
      const genreFilter = hasGenreFilter ? new Set(newFilters.genres!) : null
      const hasStyleFilter = (newFilters.styles?.length ?? 0) > 0
      const stylesList = stylesListRef.current
      const styleFilterIndices = hasStyleFilter ? new Set(newFilters.styles!.map(s => stylesList.indexOf(s)).filter(i => i >= 0)) : null
      const minScore = newFilters.min_elevated_score, minYear = newFilters.year_from, maxYear = newFilters.year_to
      const minWant = newFilters.min_want, minMedian = newFilters.min_median, maxMedian = newFilters.max_median
      for (let i = 0; i < points.length; i++) {
        const p = points[i]
        if (hasGenreFilter && !genreFilter!.has(p.g ?? '')) continue
        if (hasStyleFilter && (!p.styles || !p.styles.some(idx => styleFilterIndices!.has(idx)))) continue
        if (minScore != null && (p.score ?? 0) < minScore) continue
        if (minYear != null && (p.year ?? 0) < minYear) continue
        if (maxYear != null && (p.year ?? 0) > maxYear) continue
        if (minWant != null && (p.want ?? 0) < minWant) continue
        if (minMedian != null && (p.median ?? 0) < minMedian) continue
        if (maxMedian != null && (p.median ?? 0) > maxMedian) continue
        ids.add(p.id)
      }
      if (newFilters.max_last_sold_days != null) {
        const params = new URLSearchParams()
        params.set('max_last_sold_days', String(newFilters.max_last_sold_days))
        const res = await fetch(`/api/releases/map/filter?${params.toString()}`)
        const data = await res.json()
        const serverIds = new Set<number>(data.ids ?? [])
        for (const id of ids) if (!serverIds.has(id)) ids.delete(id)
      }
      matchedIdsRef.current = ids
      highlightModeRef.current = 'filter'; buildHlBuf(); setHighlightMode('filter'); requestDraw()
    } catch { /* silent */ }
  }

  const toggleHighlight = (m: 'wantlist' | 'collection') => {
    setHighlightMode(prev => prev === m ? 'none' : m)
    setSearchQuery(''); matchedIdsRef.current = new Set()
  }

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <h1>Sound Map{pointCount > 0 && <span className={pageStyles.countBadge}>{pointCount.toLocaleString()}</span>}</h1>
        <div className={styles.topControls}>
          <SearchInput value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery('')} placeholder="Search the map…" />
          <button className={`${styles.chip}${filterPanelOpen ? ' ' + styles.chipActive : ''}`} onClick={() => setFilterPanelOpen(o => !o)} title="Toggle filters">
            <i className="bi bi-sliders"></i><span>Filters{hasActiveMapFilters(mapFilters) ? ' •' : ''}</span>
          </button>
          {hasActiveMapFilters(mapFilters) && (
            <button className={styles.chipClear} onClick={() => handleMapFiltersChange({})} title="Clear filters"><i className="bi bi-x-lg"></i></button>
          )}
          <Select className={styles.colorSelect} value={colorMode} onChange={e => setColorMode((e.target as HTMLSelectElement).value as ColorMode)} title="Colour mode" options={COLOR_MODE_OPTIONS} data-tour="xy-map" />
        </div>
      </div>

      {filterPanelOpen && <ReleaseFilters filters={mapFilters} onFiltersChange={handleMapFiltersChange} />}

      {highlightMode !== 'none' && (
        <div className={styles.statusBar}>
          <span>
            {highlightMode === 'search'     && <><i className="bi bi-search"></i> {highlightCount.toLocaleString()} match{highlightCount !== 1 ? 'es' : ''} for &ldquo;{searchQuery}&rdquo;</>}
            {highlightMode === 'filter'     && <><i className="bi bi-sliders"></i> {highlightCount.toLocaleString()} release{highlightCount !== 1 ? 's' : ''} match filters</>}
            {highlightMode === 'wantlist'   && <><i className="bi bi-heart-fill" style={{ color: '#f87171' }}></i> Wantlist highlighted</>}
            {highlightMode === 'collection' && <><i className="bi bi-collection-fill" style={{ color: '#4ade80' }}></i> Collection highlighted</>}
          </span>
          <button className={styles.statusClear} onClick={() => { setHighlightMode('none'); setSearchQuery(''); matchedIdsRef.current = new Set() }}><i className="bi bi-x-lg"></i></button>
        </div>
      )}

      <div ref={containerRef} className={`${styles.mapContainer}${isDragging ? ' ' + styles.dragging : ''}`}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
        onMouseLeave={() => { dragStart.current = null; setIsDragging(false); hoveredIdRef.current = null; setHoverPreview(null) }}
        onWheel={onWheel} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <canvas ref={glCanvasRef} className={styles.canvas} />
        <canvas ref={fgCanvasRef} className={styles.canvas} />

        {isLoading && (
          <div className={styles.overlay}>
            <i className={`bi bi-soundwave ${styles.overlayIcon}`}></i>
            <span>Charting the sound map…</span>
            <div className={styles.overlayProgress}><div className={styles.overlayProgressBar} /></div>
            {loadingElapsed >= 3 && <span className={styles.overlayElapsed}>{loadingElapsed}s — building index for the first time</span>}
          </div>
        )}
        {noData && !isLoading && (
          <div className={styles.overlay}>
            <i className="bi bi-soundwave"></i><span>No map data yet</span>
            <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>Run <code>python scripts/build_music_map.py --publish-map</code> first</span>
          </div>
        )}

        {colorMode !== 'none' && colorLegend && (
          <div className={styles.colorLegend}>
            <span className={styles.colorLegendLabel}>{COLOR_MODE_OPTIONS.find(o => o.value === colorMode)?.label}</span>
            <div className={styles.colorLegendBar} style={{ background: BRAND_COLORMAP.css }} />
            <div className={styles.colorLegendTicks}><span>{colorLegend.min}</span><span>{colorLegend.max}</span></div>
          </div>
        )}

        <div className={styles.controls}
          onMouseDown={e => e.stopPropagation()} onMouseUp={e => e.stopPropagation()}
          onMouseMove={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
          <button className={styles.controlBtn} onClick={surpriseMe} title="Surprise me"><i className="bi bi-shuffle"></i></button>
          <button className={`${styles.controlBtn}${highlightMode === 'wantlist' ? ' ' + styles.controlBtnActive : ''}`} onClick={() => toggleHighlight('wantlist')} title="Highlight wantlist">
            <i className={`bi ${highlightMode === 'wantlist' ? 'bi-heart-fill' : 'bi-heart'}`} style={highlightMode === 'wantlist' ? { color: '#f87171' } : undefined}></i>
          </button>
          <button className={`${styles.controlBtn}${highlightMode === 'collection' ? ' ' + styles.controlBtnActive : ''}`} onClick={() => toggleHighlight('collection')} title="Highlight collection">
            <i className={`bi ${highlightMode === 'collection' ? 'bi-collection-fill' : 'bi-collection'}`} style={highlightMode === 'collection' ? { color: '#4ade80' } : undefined}></i>
          </button>
          <div className={styles.controlSep} />
          <button className={styles.controlBtn} onClick={() => zoomBy(1.4)} title="Zoom in"><i className="bi bi-plus-lg"></i></button>
          <button className={styles.controlBtn} onClick={() => zoomBy(1 / 1.4)} title="Zoom out"><i className="bi bi-dash-lg"></i></button>
          <button className={styles.controlBtn} onClick={resetCamera} title="Reset view"><i className="bi bi-fullscreen"></i></button>
        </div>

        {hoverPreview && !selectedPoint && (
          <div className={styles.hoverPreview}
            style={{
              left: Math.min(hoverPreview.x + 16, getCssDims().w - 188),
              top: Math.min(Math.max(hoverPreview.y - 60, 8), getCssDims().h - 96),
            }}>
            <div className={styles.hoverCover}>
              {hoverPreview.r.images?.[0]?.uri150 || hoverPreview.r.images?.[0]?.uri
                ? <img src={hoverPreview.r.images[0].uri150 || hoverPreview.r.images[0].uri} alt="" />
                : <div className={styles.hoverNoCover}><i className="bi bi-disc"></i></div>}
            </div>
            <div className={styles.hoverMeta}>
              <span className={styles.hoverTitle}>{hoverPreview.r.title}</span>
              <span className={styles.hoverArtist}>
                {typeof hoverPreview.r.artists === 'string'
                  ? hoverPreview.r.artists
                  : Array.isArray(hoverPreview.r.artists) ? hoverPreview.r.artists.map((a: any) => a.name || a).join(', ') : ''}
              </span>
              {hoverPreview.r.year && <span className={styles.hoverYear}>{hoverPreview.r.year}</span>}
            </div>
          </div>
        )}

        {selectedPoint && (
          <div className={styles.popup} style={{ left: popupPos.x, top: popupPos.y }}
            onMouseDown={e => e.stopPropagation()} onMouseMove={e => e.stopPropagation()}
            onMouseUp={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
            <button className={styles.popupClose} onClick={() => closePopup(true)} title="Close"><i className="bi bi-x"></i></button>
            {isLoadingDetail || !selectedDetail
              ? <div className={styles.popupLoading}><i className="bi bi-disc"></i></div>
              : <>
                  {/* Reuse the real grid card — hover the cover → playable tracks, exactly like the releases grid */}
                  <ReleaseGridCard release={selectedDetail} />

                  {selectedAudioProfile?.has_audio && (
                    <div className={styles.audioProfile}>
                      <div className={styles.audioProfileHeader}><i className="bi bi-soundwave"></i><span>Audio read</span></div>
                      <div className={styles.audioProfileTerms}>
                        {topAudioTerms(selectedAudioProfile.predicted_styles, 3).map(([term, score]) => (
                          <span key={`s-${term}`} className={styles.audioPill}>{term}<b>{Math.round(score * 100)}%</b></span>
                        ))}
                        {topAudioTerms(selectedAudioProfile.predicted_genres, 1).map(([term, score]) => (
                          <span key={`g-${term}`} className={styles.audioPillMuted}>{term}<b>{Math.round(score * 100)}%</b></span>
                        ))}
                      </div>
                    </div>
                  )}
                  {audioNeighbors && audioNeighbors.length > 0 && (
                    <div className={styles.soundsLike}>
                      <div className={styles.soundsLikeHeader}><i className="bi bi-soundwave"></i> Sounds like</div>
                      {audioNeighbors.slice(0, 3).map(n => (
                        <div key={n.id} className={styles.soundsLikeItem} onClick={() => {
                          const mp = pointsByIdRef.current.get(n.id)
                          if (mp) { cameraRef.current = { cx: mp.x, cy: mp.y, zoom: Math.max(cameraRef.current.zoom, 3) }; openPopup(mp); requestDraw() }
                        }}>
                          <span className={styles.soundsLikeTitle}>
                            {typeof n.artists === 'string' ? n.artists : Array.isArray(n.artists) ? n.artists.map((a: any) => a.name || a).join(', ') : ''}
                            {' – '}{n.title}
                          </span>
                          {n.similarity_score != null && <span className={styles.soundsLikeScore}>{(n.similarity_score * 100).toFixed(0)}%</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {audioNeighborsLoading && <div className={styles.soundsLikeLoading}><i className="bi bi-soundwave"></i> Loading similar…</div>}
                </>}
          </div>
        )}
      </div>
    </div>
  )
}
